// app/javascript/lib/utils.ts
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/&/g, '-and-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}


export function resolveBackNavigation(defaultHref: string, defaultLabel: string): { href: string; label: string } {
  if (typeof window === 'undefined') {
    return { href: defaultHref, label: defaultLabel }
  }

  const rawReturnTo = new URLSearchParams(window.location.search).get('return_to')
  if (!rawReturnTo) {
    return { href: defaultHref, label: defaultLabel }
  }

  const cleanReturnTo = rawReturnTo.replace(/^https?:\/\/[^\/]+/, '')
  if (!cleanReturnTo.startsWith('/')) {
    return { href: defaultHref, label: defaultLabel }
  }

  // Extract path without query parameters
  const pathWithoutQuery = cleanReturnTo.split('?')[0]
  const segments = pathWithoutQuery.split('/').filter(Boolean)

  if (segments.length === 0) {
    return { href: cleanReturnTo, label: defaultLabel }
  }

  const lastSegment = segments[segments.length - 1]
  const isNumericOrId = /^\d+$/.test(lastSegment) || (lastSegment.length > 20 && !lastSegment.includes('-'))

  const resourceSegment = (isNumericOrId && segments.length >= 2) ? segments[segments.length - 2] : lastSegment

  const resourceNames: Record<string, { singular: string; plural: string }> = {
    admin: { singular: 'Dashboard', plural: 'Dashboard' },
    users: { singular: 'User', plural: 'Users' },
    questions: { singular: 'Question', plural: 'Question Bank' },
    tests: { singular: 'Test', plural: 'Tests' },
    exams: { singular: 'Exam', plural: 'Exams' },
    subjects: { singular: 'Subject', plural: 'Subjects' },
    standards: { singular: 'Standard', plural: 'Standards' },
    syllabi: { singular: 'Syllabus', plural: 'Syllabi' },
    chapters: { singular: 'Chapter', plural: 'Chapters' },
    sections: { singular: 'Section', plural: 'Sections' },
  }

  const mapped = resourceNames[resourceSegment.toLowerCase()]
  if (mapped) {
    const label = isNumericOrId ? `Back to ${mapped.singular}` : `Back to ${mapped.plural}`
    return { href: cleanReturnTo, label }
  }

  const formattedSegment = resourceSegment.charAt(0).toUpperCase() + resourceSegment.slice(1).replace(/_/g, ' ')
  const label = `Back to ${formattedSegment}`
  return { href: cleanReturnTo, label }
}
